import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Id, toast } from "react-toastify";
import DesignStateService from "../service/DesignStateService";
import ImageService from "../service/ImageService";
import TeamService from "../service/TeamService";
import {
  ColorBleedingItem,
  ColorEndOfLifeItem,
  DeprecatedDesignItem,
  DesignFamily,
  DesignFromGenerator,
  DesignProduct,
  DesignStateType,
  EditorSelection,
  GLASS_MAGNIFIER_BACKGROUND_COLORS,
  ProductDefinition,
} from "../types";
import { ROUTES } from "../constant/common.constant";
import { useDesignFetcher } from "./DesignsFetcherHooks";
import { useThemeBreakpoints } from "./themeUtils";
import DesignService from "../service/DesignService";
import { useMultiplayer } from "../context/Multiplayer/useMultiplayer";
import { MultiplayerBasePayloadType } from "../types/Multiplayer.types";
import { getDesignStateFromMessage } from "./Multiplayer.utils";
import { useDesign } from "../context/Design/useDesign";

export enum DESIGN_CHANGE_ENUM {
  REFRESH = "refresh",
  ADD_DECORATION = "addDecoration",
  ON_REMOVE = "removeDecoration",
  ON_REINITIALIZE = "reinitializeDecoration",
  SET_COLOR = "setColor",
  SET_COLOR_COMBO = "setColorCombo",
  UPDATE_PRINT_COLOR = "updatePrintColor",
  SET_STYLE = "setStyle",
  SET_MODEL = "setModel",
  SET_DECORATION_LOCATION = "setDecorationLocation",
  SET_SIZE = "setDecorationSize",
  SET_DECORATION_TAG_TEXTURE = "setDecorationTagTextureName",
  SET_DECORATION_TAG = "setDecorationTagName",
  SET_DECORATION_TAG_BACKGROUND_COLOR = "setDecorationTagBackgroundColorName",
  SET_DECORATION_EMBROIDERY_PATCH_BACKGROUND_COLOR = "setDecorationPatchBackgroundColorName",
  SET_DECORATION_EMBROIDERY_PATCH_STITCH_COLOR = "setDecorationPatchStitchColorName",
  SET_REPEATING_PATTERN_DETAILS = "setDecorationRepeatingPattern",
  SET_DECORATION_ACTIVE = "setDecorationActive",
  SET_DECORATION_ASSET = "setDecorationAsset",
  CHANGE_DECORATION_TYPE = "changeDecorationType",
  MODIFY_ASSET_ROTATION = "modifyAssetRotation",
}

export const DEPRECATION_TYPE_ENUM = {
  DEPRECATED_STYLE: "deprecatedStyle",
  DEPRECATED_COLOR: "deprecatedColor",
};

export const useDesignState = (
  designId?: string,
  skipPropagateDesignStateUpdate?: boolean
): DesignStateType => {
  const { t } = useTranslation();
  const { isMobile } = useThemeBreakpoints();
  const [isProductDefLoading, setIsProductDefLoading] = useState(true);
  const [designWarning, setDesignWarning] = useState(false);
  const [migrationNeeded, setMigrationNeeded] = useState(false);
  const [containsColorsEndOfLife, setContainsColorsEndOfLife] = useState(false);
  const [colorWarning, setColorWarning] = useState(false);
  const [productVersionNeedUpgrade, setProductVersionNeedUpgrade] =
    useState(false);
  const [deprecatedDesignItems, setDeprecatedDesignItems] = useState<
    DeprecatedDesignItem[]
  >([]);
  const [colorBleedingItems, setColorBleedingItems] = useState<
    ColorBleedingItem[]
  >([]);
  const [colorEndOfLifeItems, setColorEndOfLifeItems] = useState<
    ColorEndOfLifeItem[]
  >([]);
  const [loadingFailedErrorKey, setLoadingFailedErrorKey] = useState<
    Id | undefined
  >();
  const [hasLoadingFailed, setLoadingFailed] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPartsDefinitionLoading, setIsPartsDefinitionLoading] =
    useState(false);
  const [designState, setDesignState] = useState<DesignFromGenerator | null>(
    null
  );
  const [productDefinition, setProductDefinition] =
    useState<ProductDefinition | null>(null);
  const {
    onDesignUpdate: onMultiplayerDesignUpdate,
    removeDesignUpdate: removeMultiplayerDesignUpdate,
  } = useMultiplayer();
  const { updateDesignImages, design } = useDesign();
  const { fetchProductDefinition } = useDesignFetcher();

  const [selection, setSelection] = useState<EditorSelection>({
    selectedDesignId: "",
    selectedPart: "",
    selectedElementName: "",
    selectedIndex: "",
  });

  const initializeSelection = useCallback(() => {
    let firstPartName = "";
    let firstPartMainElementName = "";
    if (designState?.design?.parts && designState.design.parts.length > 0) {
      firstPartName = designState.design.parts[0].name;
      firstPartMainElementName = designState.design.parts[0].mainElement;
    }
    setSelection({
      selectedDesignId: designId,
      selectedPart: firstPartName,
      selectedElementName: firstPartMainElementName,
      selectedIndex: "",
    });
  }, [designState, designId]);

  useEffect(() => {
    if (
      isProductDefLoading ||
      !designState ||
      !designState.design ||
      designState.designId !== designId ||
      (selection.selectedDesignId === designId &&
        !isMobile &&
        !!selection.selectedPart) ||
      isMobile
    ) {
      return;
    }

    initializeSelection();
  }, [
    designState,
    designId,
    selection.selectedDesignId,
    isProductDefLoading,
    isMobile,
    initializeSelection,
    selection.selectedPart,
  ]);

  const onSelectionChange = (
    partName: string,
    attributeName: string,
    index?: string
  ) => {
    setSelection({
      selectedDesignId: designId,
      selectedPart: partName,
      selectedElementName: attributeName,
      selectedIndex: index ?? "",
    });
  };

  const updateProductDef = useCallback(
    async (designFamily?: DesignFamily, designProduct?: DesignProduct) => {
      try {
        if (!designFamily || !designProduct) {
          return;
        }
        setIsProductDefLoading(true);
        const res = await fetchProductDefinition(designFamily, designProduct);
        const productDefinition = res.productDefinition as ProductDefinition;
        setProductDefinition(res.productDefinition);
        setProductVersionNeedUpgrade(productDefinition.status === "deprecated");

        return {
          tagOptions: res.tagOptions,
          repeatingPatternsOptions: res.repeatingPatternsOptions,
        };
      } catch (e: any) {
        if (e.response && e.response.data.errorKey) {
          setLoadingFailedErrorKey(e.response.data.errorKey);
        } else {
          setLoadingFailedErrorKey("unknown");
        }

        setProductDefinition(null);
        setLoadingFailed(true);
      } finally {
        setIsProductDefLoading(false);
      }
    },
    [fetchProductDefinition]
  );

  const updateDecorationPreviewBackgroundColor = useCallback(
    (
      decorationId: string,
      previewBackgroundColor: GLASS_MAGNIFIER_BACKGROUND_COLORS
    ) => {
      setDesignState((prev) => {
        if (!prev) {
          return null;
        }
        return {
          ...prev,
          design: {
            ...prev.design,
            decorations: prev.design.decorations.map((decoration) =>
              decoration.id === decorationId
                ? { ...decoration, previewBackgroundColor }
                : decoration
            ),
          },
        };
      });
    },
    []
  );
  const updateLocalDesignState = useCallback(
    async ({ skipLoading = false }: { skipLoading?: boolean } = {}) => {
      try {
        const { data } = await DesignStateService.getDesignState(designId);
        if (!skipLoading) {
          setIsPartsDefinitionLoading(true);
        }
        await updateProductDef(data.design.family, data.design.product);
        setDesignState(data);
        //propagate the newest design images to the designs in redux store
        updateDesignImages(data.images);
      } catch (e: any) {
        if (e.response && e.response.data.errorKey) {
          setLoadingFailedErrorKey(e.response.data.errorKey);
        } else {
          setLoadingFailedErrorKey("unknown");
        }
        setDesignState(null);
        setLoadingFailed(true);
      } finally {
        setIsProductDefLoading(false);
        setIsPartsDefinitionLoading(false);
      }
    },
    [designId, updateDesignImages, updateProductDef]
  );

  useEffect(() => {
    updateLocalDesignState();
  }, [updateLocalDesignState]);

  // Every time an update is done, verify everything is still available.
  useEffect(() => {
    if (!designState || !designState.design) {
      return;
    }

    // Check if the design contains color or styles no longer available.
    const deprecatedItems = [];
    const colorBleed = [];
    const colorEndOfLifeItems = [];
    setDesignWarning(false);
    setMigrationNeeded(!!designState.design.needsMigration);
    setContainsColorsEndOfLife(!!designState.design.containsColorsEndOfLife);
    setColorWarning(
      !!designState.design?.containsColorsLikelyToBleedOnEachOther
    );

    for (const part of designState.design.parts) {
      for (const element of part.elements) {
        if (element.style.deprecated) {
          setDesignWarning(true);
          deprecatedItems.push({
            type: DEPRECATION_TYPE_ENUM.DEPRECATED_STYLE,
            partName: part.name,
            elementName: element.name,
            styleName: element.style.name,
          });
        }
        for (const layer of element.style.layers) {
          if (layer.deprecated) {
            setDesignWarning(true);
            deprecatedItems.push({
              type: DEPRECATION_TYPE_ENUM.DEPRECATED_COLOR,
              partName: part.name,
              elementName: element.name,
              styleName: element.style.name,
              layerName: layer.name,
            });
          }
          // When there is bleeding, flag all the layers that either bleed or are bled on.
          if (
            designState.design?.containsColorsLikelyToBleedOnEachOther &&
            (layer.containsColorsThatTendsToBeBledOn ||
              layer.containsColorsThatTendsToBleed)
          ) {
            colorBleed.push({
              bleeding: layer.containsColorsThatTendsToBleed,
              bledOn: layer.containsColorsThatTendsToBeBledOn,
              partName: part.name,
              elementName: element.name,
              styleName: element.style.name,
              layerName: layer.name,
            });
          }
          if (layer.containsColorsEndOfLife) {
            colorEndOfLifeItems.push({
              partName: part.name,
              elementName: element.name,
              styleName: element.style.name,
              layerName: layer.name,
            });
          }
        }
      }
    }
    setDeprecatedDesignItems(deprecatedItems);
    setColorBleedingItems(colorBleed);
    setColorEndOfLifeItems(colorEndOfLifeItems);
  }, [
    designState,
    setDesignWarning,
    setMigrationNeeded,
    setDeprecatedDesignItems,
    setContainsColorsEndOfLife,
    setColorEndOfLifeItems,
  ]);

  const updateDesign = useCallback(
    async (payload: {
      action: DESIGN_CHANGE_ENUM;
      designId?: string;
      color?: any;
      group?: string;
      name?: string;
      transparent?: any;
      indexOfColor?: number;
      decorationId?: string;
      tagName?: string;
      colorName?: string;
      repeatingDetails?: any;
      positionName?: string;
      active?: boolean;
      textureName?: string;
      size?: any;
      partName?: string;
      assetId?: string;
      elementName?: string;
      assetVersionId?: string;
      styleName?: string;
      type?: string;
      lastStyleName?: string;
      layerName?: string;
      colorComboName?: string;
      lastColor?: string;
      model?: DesignProduct;
      angle?: number;
    }) => {
      if (design?.locked) {
        return null;
      }
      try {
        console.time("calling updateDesign");
        const resUpdate = await DesignStateService.updateDesign(
          payload,
          designState?.design?.product
        );
        console.timeEnd("calling updateDesign");
        return resUpdate;
      } catch (err: any) {
        console.error("Error sending update of design state", err);
        if (err.response && err.response.data && err.response.data.errorKey) {
          toast.error(t(`errors.toast.${err.response.data.errorKey}`), {
            toastId: err.response.data.errorKey,
          });
        }
        updateLocalDesignState();
      }
    },
    [design?.locked, designState?.design?.product, updateLocalDesignState, t]
  );

  const getProductPartsDefinition = useCallback(() => {
    if (isProductDefLoading || !designState || !productDefinition) {
      return [];
    }
    return productDefinition.parts;
  }, [designState, productDefinition, isProductDefLoading]);

  const partsDefinition = getProductPartsDefinition();

  const getFabricsDefinition = useCallback(() => {
    if (isProductDefLoading || !designState || !productDefinition) {
      return [];
    }
    return productDefinition.fabrics;
  }, [designState, productDefinition, isProductDefLoading]);

  const fabricsDefinition = getFabricsDefinition();

  const getProductAvailableDecorationsDefinition = useCallback(() => {
    if (isProductDefLoading || !designState || !productDefinition) {
      return [];
    }
    return productDefinition.decorations;
  }, [designState, productDefinition, isProductDefLoading]);

  const availableDecorationsDefinition =
    getProductAvailableDecorationsDefinition();

  const getDesignValueOfSelectedElement = useCallback(() => {
    if (!designState || !designState.design || !designState.design.parts) {
      return undefined;
    }
    const part = designState.design.parts.find(
      (p) => p.name === selection.selectedPart
    );
    if (!part) {
      return undefined;
    }
    const attribute = part.elements.find(
      (e) => e.name === selection.selectedElementName
    );
    if (!attribute) {
      return undefined;
    }
    return attribute;
  }, [designState, selection.selectedPart, selection.selectedElementName]);

  const designValueOfSelectedElement = getDesignValueOfSelectedElement();

  const getSelectedPartElementDef = useCallback(() => {
    if (selection.selectedPart === "Decoration") {
      return undefined;
    }

    const partDefFound = partsDefinition?.find(
      (partDef) => partDef.name === selection.selectedPart
    );

    if (!partDefFound) {
      return undefined;
    }

    const elementDefFound = partDefFound.elements.find(
      (a) => a.name === selection.selectedElementName
    );

    //FIXME: For now we add all the fabrics (colors) definition to the elementDef, so it's simple to use when needed
    //FIXME: but we should create another mechanism to fetch fabrics once and store in memory and available in a store
    if (elementDefFound) {
      elementDefFound.fabrics = fabricsDefinition;
    }

    return elementDefFound;
  }, [
    selection.selectedElementName,
    selection.selectedPart,
    partsDefinition,
    fabricsDefinition,
  ]);

  const selectedPartElementDef = getSelectedPartElementDef();

  const getDesignPricing = useCallback(() => {
    if (
      isProductDefLoading ||
      !designState ||
      !designState.customizationPricing
    ) {
      return;
    }

    return designState.customizationPricing;
  }, [isProductDefLoading, designState]);
  const designPricing = getDesignPricing();

  const getDeliveryTime = useCallback(() => {
    if (isProductDefLoading || !designState || !designState.deliveryTime) {
      return;
    }

    return designState.deliveryTime;
  }, [isProductDefLoading, designState]);
  const deliveryTime = getDeliveryTime();

  const getSiblingProducts = useCallback(() => {
    if (isProductDefLoading || !designState || !designState.siblingProducts) {
      return;
    }

    return designState.siblingProducts;
  }, [isProductDefLoading, designState]);
  const siblingProducts = getSiblingProducts();

  const getSizesOption = useCallback(() => {
    if (isProductDefLoading || !designState || !productDefinition) {
      return;
    }

    return productDefinition.sizes;
  }, [isProductDefLoading, designState, productDefinition]);

  const sizesOption = getSizesOption();

  const onDesignChangeUndebounce = useCallback(
    async (action: DESIGN_CHANGE_ENUM, data: any) => {
      setIsUpdating(true);

      let updateDesignRes = null;
      let resetProductDef = null;

      switch (action) {
        case DESIGN_CHANGE_ENUM.REFRESH: {
          updateDesignRes = await updateDesign({
            action,
            designId,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.ADD_DECORATION: {
          updateDesignRes = await updateDesign({
            action,
            group: data.group,
            name: data.name,
            decorationId: data.decorationId,
            designId,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.ON_REMOVE: {
          updateDesignRes = await updateDesign({
            designId,
            action,
            decorationId: data.decorationId,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.ON_REINITIALIZE: {
          updateDesignRes = await updateDesign({
            designId,
            action,
            decorationId: data.decorationId,
            name: data.name,
            group: data.group,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.UPDATE_PRINT_COLOR: {
          updateDesignRes = await updateDesign({
            action,
            designId,
            color: data.color,
            transparent: data.transparent,
            indexOfColor: data.index,
            decorationId: data.decorationId,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_DECORATION_TAG: {
          updateDesignRes = await updateDesign({
            action,
            designId,
            decorationId: data.decorationId,
            tagName: data.tagName,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_DECORATION_TAG_BACKGROUND_COLOR: {
          updateDesignRes = await updateDesign({
            action,
            designId,
            decorationId: data.decorationId,
            colorName: data.colorName,
          });
          break;
        }
        case DESIGN_CHANGE_ENUM.SET_DECORATION_EMBROIDERY_PATCH_BACKGROUND_COLOR: {
          updateDesignRes = await updateDesign({
            action,
            designId,
            decorationId: data.decorationId,
            colorName: data.colorName,
          });
          break;
        }
        case DESIGN_CHANGE_ENUM.SET_DECORATION_EMBROIDERY_PATCH_STITCH_COLOR: {
          updateDesignRes = await updateDesign({
            action,
            designId,
            decorationId: data.decorationId,
            colorName: data.colorName,
          });
          break;
        }
        case DESIGN_CHANGE_ENUM.SET_REPEATING_PATTERN_DETAILS: {
          updateDesignRes = await updateDesign({
            action,
            designId,
            decorationId: data.decorationId,
            repeatingDetails: data.repeatingDetails,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_DECORATION_LOCATION: {
          updateDesignRes = await updateDesign({
            designId,
            action,
            decorationId: data.decorationId,
            positionName: data.positionName,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_DECORATION_ACTIVE: {
          updateDesignRes = await updateDesign({
            designId,
            action,
            decorationId: data.decorationId,
            active: data.active,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_DECORATION_TAG_TEXTURE: {
          updateDesignRes = await updateDesign({
            designId,
            action,
            decorationId: data.decorationId,
            textureName: data.textureName,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_SIZE: {
          updateDesignRes = await updateDesign({
            designId,
            action,
            decorationId: data.decorationId,
            size: data.size,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_STYLE: {
          updateDesignRes = await updateDesign({
            designId,
            action,
            partName: selection.selectedPart,
            elementName: selection.selectedElementName,
            styleName: data.styleName,
            lastStyleName: data.lastStyleName,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_MODEL: {
          updateDesignRes = await updateDesign({
            designId,
            action,
            model: data.model,
          });
          resetProductDef = true;
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_COLOR: {
          updateDesignRes = await updateDesign({
            designId,
            action,
            partName: selection.selectedPart,
            elementName: selection.selectedElementName,
            styleName: data.styleName,
            layerName: data.layerName,
            colorName: data.colorName,
            lastColor: data.lastColor,
            active: data.active,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_COLOR_COMBO: {
          updateDesignRes = await updateDesign({
            designId,
            action,
            partName: selection.selectedPart,
            elementName: selection.selectedElementName,
            styleName: data.styleName,
            colorComboName: data.colorComboName,
          });
          break;
        }

        case DESIGN_CHANGE_ENUM.SET_DECORATION_ASSET: {
          updateDesignRes = await updateDesign({
            action,
            designId,
            decorationId: data.decorationId,
            assetId: data.assetId,
            assetVersionId: data.assetVersionId,
            name: data.name,
            type: data.type,
          });
          break;
        }
        case DESIGN_CHANGE_ENUM.CHANGE_DECORATION_TYPE: {
          updateDesignRes = await updateDesign({
            action,
            designId,
            decorationId: data.decorationId,
            assetId: data.assetId,
            assetVersionId: data.assetVersionId,
            name: data.name,
            type: data.type,
          });
          break;
        }
        case DESIGN_CHANGE_ENUM.MODIFY_ASSET_ROTATION: {
          updateDesignRes = await updateDesign({
            action,
            designId,
            decorationId: data.decorationId,
            angle: data.rotation,
          });
          break;
        }
        default: {
          console.error("action not supported!", action);
          break;
        }
      }

      if (updateDesignRes && updateDesignRes.data) {
        //update design.updatedAt in backend
        if (!skipPropagateDesignStateUpdate) {
          await DesignService.propagateDesignStateUpdate({
            designId: designId ?? "",
          });
        }
        const newDesignState = updateDesignRes.data;
        updateDesignImages(newDesignState.images);
        //TODO: We should probably just use the newDesignState
        setDesignState(newDesignState);
        if (resetProductDef) {
          updateProductDef(
            newDesignState.design.family,
            newDesignState.design.product
          );
        }
      }
      setIsUpdating(false);
    },
    [
      updateDesign,
      designId,
      skipPropagateDesignStateUpdate,
      selection.selectedPart,
      selection.selectedElementName,
      updateDesignImages,
      updateProductDef,
    ]
  );

  const onDesignChange = debounce(onDesignChangeUndebounce, 500);

  /**
   * Function called when a decoration is changing type (Like from print to detailedprint)
   */
  const onAssetTypeChange = useCallback(
    async ({
      decorationId,
      assetId,
      assetType,
      name,
    }: {
      decorationId: string;
      assetId: string;
      assetType: string;
      name: string;
    }) => {
      //Create the asset type
      const { assetVersionId, errorKey } = await ImageService.createAsset(
        assetId,
        assetType
      );

      if (errorKey) {
        return { errorKey };
      }

      return await onDesignChangeUndebounce(
        DESIGN_CHANGE_ENUM.CHANGE_DECORATION_TYPE,
        {
          designId,
          decorationId,
          assetId,
          assetVersionId,
          type: assetType,
          name,
        }
      );
    },
    [onDesignChangeUndebounce, designId]
  );

  useEffect(() => {
    const handleDesignUpdate = (payload: MultiplayerBasePayloadType) => {
      updateLocalDesignState({ skipLoading: true });
      const newDesignState = getDesignStateFromMessage(payload);
      updateDesignImages(newDesignState.images);
    };
    onMultiplayerDesignUpdate(handleDesignUpdate);

    return () => {
      removeMultiplayerDesignUpdate(handleDesignUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designState]);

  return {
    designDecorationsFromDesignState: designState?.design?.decorations,
    designFromDesignState: designState?.design,
    images: designState?.images,
    designWarning,
    migrationNeeded,
    colorWarning,
    containsColorsEndOfLife,
    productVersionNeedUpgrade,
    deprecatedDesignItems,
    colorBleedingItems,
    colorEndOfLifeItems,
    isProductDefLoading: isProductDefLoading,
    setIsProductDefLoading: setIsProductDefLoading,
    hasLoadingFailed,
    loadingFailedErrorKey,
    isUpdating,
    setIsUpdating,
    onDesignChange: onDesignChange,
    onAssetTypeChange,
    partsDefinition,
    availableDecorationsDefinition,
    designValueOfSelectedElement,
    selectedPartElementDef,
    designPricing,
    deliveryTime,
    siblingProducts,
    sizesOption,
    onSelectionChange,
    updateDecorationPreviewBackgroundColor,
    selection,
    isPartsDefinitionLoading,
    productLine: designState?.productLine,
    shipping: productDefinition?.shipping,
    productDefinition: productDefinition || undefined,
  };
};

export const useTokenInfo = (token?: string) => {
  const navigate = useNavigate();
  const [tokenInfo, setTokenInfo] = useState<any | null>(null);

  useEffect(() => {
    const fetchTokenInfo = async (token?: string) => {
      if (!token) {
        return;
      }

      try {
        const {
          data: { data: info },
        } = await TeamService.getTokenInfo(token);

        setTokenInfo(info);
      } catch (error) {
        navigate(ROUTES.LOGIN, {
          state: { inviteLinkExpired: true },
        });
      }
    };
    fetchTokenInfo(token);
  }, [navigate, token]);

  return tokenInfo;
};
